import { AppUser, UserFilters } from "../types/user";
import config from "../config/environment";
import { ApiService } from "./apiService";

// Mock data for development - will be replaced by real API calls
const mockUsers: AppUser[] = [];

export class UserService {
  private static readonly ENDPOINTS = {
    USERS: "/users",
    USER_DETAILS: (id: string) => `/v1/users/${id}/details`,
    USER_STATS: "/users/stats",
  };

  /**
   * Fetch users with optional filters
   */
  static async getUsers(filters?: Partial<UserFilters>): Promise<AppUser[]> {
    if (config.enableMockData) {
      // Mock implementation with filtering
      return new Promise((resolve) => {
        setTimeout(() => {
          let filteredUsers = [...mockUsers];

          if (filters?.search) {
            const searchTerm = filters.search.toLowerCase();
            filteredUsers = filteredUsers.filter(
              (user) =>
                user.name.toLowerCase().includes(searchTerm) ||
                user.email.toLowerCase().includes(searchTerm)
            );
          }

          if (filters?.status) {
            filteredUsers = filteredUsers.filter(
              (user) => user.status === filters.status
            );
          }

          if (filters?.subscriptionPlan) {
            filteredUsers = filteredUsers.filter(
              (user) => user.subscriptionPlan === filters.subscriptionPlan
            );
          }

          if (filters?.userType) {
            if (filters.userType === "real") {
              filteredUsers = filteredUsers.filter(
                (user) => user.is_test_user === false
              );
            } else if (filters.userType === "test") {
              filteredUsers = filteredUsers.filter(
                (user) => user.is_test_user === true
              );
            }
          }

          if (filters?.platformFilter && filters.platformFilter !== "all") {
            filteredUsers = filteredUsers.filter(
              (user) => user.deviceType === filters.platformFilter
            );
          }

          if (filters?.dateRange?.startDate) {
            const startDate = new Date(filters.dateRange.startDate);
            filteredUsers = filteredUsers.filter(
              (user) => new Date(user.joinedDate || user.createdAt) >= startDate
            );
          }

          if (filters?.dateRange?.endDate) {
            const endDate = new Date(filters.dateRange.endDate);
            filteredUsers = filteredUsers.filter(
              (user) => new Date(user.joinedDate || user.createdAt) <= endDate
            );
          }

          if (filters?.lastActivityDateRange?.startDate) {
            const startDate = new Date(filters.lastActivityDateRange.startDate);
            filteredUsers = filteredUsers.filter(
              (user) =>
                user.last_activity_at && new Date(user.last_activity_at) >= startDate
            );
          }

          if (filters?.lastActivityDateRange?.endDate) {
            const endDate = new Date(filters.lastActivityDateRange.endDate);
            filteredUsers = filteredUsers.filter(
              (user) =>
                user.last_activity_at && new Date(user.last_activity_at) <= endDate
            );
          }

          if (filters?.summaryFilter) {
            if (filters.summaryFilter === "no_summary") {
              filteredUsers = filteredUsers.filter(
                (user) => !user.totalSummaries || user.totalSummaries === 0
              );
            } else if (filters.summaryFilter === "At Least One Summary") {
              filteredUsers = filteredUsers.filter(
                (user) => user.totalSummaries && user.totalSummaries > 0
              );
            }
          }

          if (filters?.excludeAppleLogin) {
            // Mock implementation - assume some users have Apple login
            filteredUsers = filteredUsers.filter(
              (user) => user.email && !user.email.includes("apple")
            );
          }

          if (filters?.showOnlyDeletedUsers === true) {
            // Mock implementation - show only deleted users
            filteredUsers = filteredUsers.filter(
              (user) => (user as any).is_account_deleted === true
            );
          }

          if (filters?.showOnlyNonVerifiedUsers === true) {
            // Mock implementation - show only non-verified users
            filteredUsers = filteredUsers.filter(
              (user) => (user as any).is_verified !== 1
            );
          }

          resolve(filteredUsers);
        }, 800);
      });
    }

    // Backend API implementation
    try {
      // Build query parameters from filters
      const params: any = {};

      if (filters?.status && filters.status !== "") {
        // Backend expects `user_status` for this filter
        params.user_status = filters.status;
      }

      if (filters?.search && filters.search.trim() !== "") {
        params.search = filters.search.trim();
      }

      if (filters?.userType && filters.userType !== "") {
        params.userType = filters.userType;
      }

      if (
        filters?.platformFilter &&
        filters.platformFilter !== "" &&
        filters.platformFilter !== "all"
      ) {
        params.platformFilter = filters.platformFilter;
      }

      if (filters?.dateRange?.startDate) {
        params.dateFrom = filters.dateRange.startDate;
      }

      if (filters?.dateRange?.endDate) {
        params.dateTo = filters.dateRange.endDate;
      }

      if (filters?.lastActivityDateRange?.startDate) {
        params.lastActivityFrom = filters.lastActivityDateRange.startDate;
      }

      if (filters?.lastActivityDateRange?.endDate) {
        params.lastActivityTo = filters.lastActivityDateRange.endDate;
      }

      if (filters?.excludeAppleLogin) {
        params.excludeAppleLogin = true;
      }

      if (filters?.showOnlyDeletedUsers === true) {
        params.showOnlyDeletedUsers = true;
      }

      if (filters?.showOnlyNonVerifiedUsers === true) {
        params.showOnlyNonVerifiedUsers = true;
      }

      if (filters?.subscriptionPlan && filters.subscriptionPlan !== "") {
        params.subscriptionPlan = filters.subscriptionPlan;
      }

      if (filters?.summaryFilter && filters.summaryFilter !== "") {
        params.summaryFilter = filters.summaryFilter;
      }
      // Fetch users from backend API
      const users = await ApiService.get<AppUser[]>(this.ENDPOINTS.USERS, {
        params,
      });

      // Apply post-processing filters that require additional logic
      let filteredUsers = users || [];

      // Filter by countries
      if (filters?.countries && filters.countries.length > 0) {
        filteredUsers = filteredUsers.filter((user) => {
          return filters.countries?.includes(user.country || "");
        });
      }

      return filteredUsers;
    } catch (error) {
      console.error("Error fetching users from API:", error);
      throw error;
    }
  }

  /**
   * Get user by ID
   */
  static async getUserById(id: string): Promise<AppUser> {
    if (config.enableMockData) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const user = mockUsers.find((u) => u.id === id);
          if (user) {
            resolve(user);
          } else {
            reject(new Error("User not found"));
          }
        }, 500);
      });
    }

    try {
      // Get user from backend API
      const user = await ApiService.get<AppUser>(
        this.ENDPOINTS.USER_DETAILS(id)
      );

      if (!user) {
        throw new Error("User not found");
      }

      return user;
    } catch (error) {
      console.error(`Error fetching user ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create new user
   */
  static async createUser(
    userData: Omit<AppUser, "id" | "joinedDate">
  ): Promise<AppUser> {
    if (config.enableMockData) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const newUser: AppUser = {
            ...userData,
            id: Date.now().toString(),
            joinedDate: new Date().toISOString(),
          };
          mockUsers.push(newUser);
          resolve(newUser);
        }, 800);
      });
    }

    try {
      // For now, throw an error since user creation logic needs to be implemented
      // This should be enhanced to actually create a user in the database
      throw new Error("User creation not yet implemented");
    } catch (error) {
      console.error("Error creating user:", error);
      throw error;
    }
  }

  /**
   * Update user test status using direct SQL query (bypasses RLS)
   */
  static async updateUserTestStatus(
    userId: string,
    isTestUser: boolean
  ): Promise<boolean> {
    if (config.enableMockData) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const userIndex = mockUsers.findIndex((u) => u.id === userId);
          if (userIndex !== -1) {
            mockUsers[userIndex].is_test_user = isTestUser;
            resolve(true);
          } else {
            resolve(false);
          }
        }, 500);
      });
    }

    try {
      // Update user test status via backend API
      await ApiService.patch(this.ENDPOINTS.USER_DETAILS(userId), {
        is_test_user: isTestUser,
      });
      return true;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update user
   */
  static async updateUser(
    id: string,
    userData: Partial<AppUser>
  ): Promise<AppUser> {
    if (config.enableMockData) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const userIndex = mockUsers.findIndex((u) => u.id === id);
          if (userIndex !== -1) {
            mockUsers[userIndex] = { ...mockUsers[userIndex], ...userData };
            resolve(mockUsers[userIndex]);
          } else {
            reject(new Error("User not found"));
          }
        }, 800);
      });
    }

    try {
      // Update user via backend API
      const updatedUser = await ApiService.patch<AppUser>(
        this.ENDPOINTS.USER_DETAILS(id),
        userData
      );

      if (!updatedUser) {
        throw new Error("User not found");
      }

      return updatedUser;
    } catch (error) {
      console.error(`Error updating user ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete user
   */
  static async deleteUser(id: string): Promise<void> {
    if (config.enableMockData) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const userIndex = mockUsers.findIndex((u) => u.id === id);
          if (userIndex !== -1) {
            mockUsers.splice(userIndex, 1);
            resolve();
          } else {
            reject(new Error("User not found"));
          }
        }, 600);
      });
    }

    try {
      // Delete user via backend API
      await ApiService.delete(this.ENDPOINTS.USER_DETAILS(id));
    } catch (error) {
      console.error(`Error deleting user ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get user statistics
   */
  static async getUserStats(): Promise<{
    totalUsers: number;
    activeUsers: number;
    newUsersThisMonth: number;
    subscriptionDistribution: Record<string, number>;
  }> {
    if (config.enableMockData) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const stats = {
            totalUsers: mockUsers.length,
            activeUsers: mockUsers.filter((u) => u.status === "active").length,
            newUsersThisMonth: mockUsers.filter((u) => {
              if (!u.joinedDate) return false;
              const joinedDate = new Date(u.joinedDate);
              const now = new Date();
              return (
                joinedDate.getMonth() === now.getMonth() &&
                joinedDate.getFullYear() === now.getFullYear()
              );
            }).length,
            subscriptionDistribution: mockUsers.reduce((acc, user) => {
              const plan = user.subscriptionPlan || "free";
              acc[plan] = (acc[plan] || 0) + 1;
              return acc;
            }, {} as Record<string, number>),
          };
          resolve(stats);
        }, 500);
      });
    }

    try {
      // Get user statistics from backend API
      const stats = await ApiService.get<{
        totalUsers: number;
        activeUsers: number;
        newUsersThisMonth: number;
        subscriptionDistribution: Record<string, number>;
      }>(this.ENDPOINTS.USER_STATS);

      return stats;
    } catch (error) {
      console.error("Error fetching user stats:", error);
      throw error;
    }
  }

  /**
   * Assign free trial to user
   */
  static async assignFreeTrial(userId: string): Promise<AppUser> {
    if (config.enableMockData) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const userIndex = mockUsers.findIndex((u) => u.id === userId);
          if (userIndex !== -1) {
            mockUsers[userIndex].subscriptionPlan = "personal";
            mockUsers[userIndex].subscriptionType = "trial";
            resolve(mockUsers[userIndex]);
          } else {
            reject(new Error("User not found"));
          }
        }, 800);
      });
    }

    try {
      // For now, return a mock response since free trial assignment logic needs to be implemented
      // This should be enhanced to actually assign a free trial in the database
      throw new Error("Free trial assignment not yet implemented");
    } catch (error) {
      console.error(`Error assigning free trial to user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Fetch users with optional filters - OPTIMIZED VERSION
   * Uses single RPC call instead of multiple calls per user
   */
  static async getUsersOptimized(
    filters?: Partial<UserFilters>,
    page: number = 1,
    limit: number = 10,
    excludeInactiveUsers: boolean = false
  ): Promise<{
    users: AppUser[];
    statistics?: any;
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    if (config.enableMockData) {
      // Mock implementation with filtering
      return new Promise((resolve) => {
        setTimeout(() => {
          let filteredUsers = [...mockUsers];

          if (filters?.search) {
            const searchTerm = filters.search.toLowerCase();
            filteredUsers = filteredUsers.filter(
              (user) =>
                user.name.toLowerCase().includes(searchTerm) ||
                user.email.toLowerCase().includes(searchTerm)
            );
          }

          if (filters?.status) {
            filteredUsers = filteredUsers.filter(
              (user) => user.status === filters.status
            );
          }

          if (filters?.subscriptionPlan) {
            filteredUsers = filteredUsers.filter(
              (user) => user.subscriptionPlan === filters.subscriptionPlan
            );
          }

          if (filters?.userType) {
            if (filters.userType === "real") {
              filteredUsers = filteredUsers.filter(
                (user) => user.is_test_user === false
              );
            } else if (filters.userType === "test") {
              filteredUsers = filteredUsers.filter(
                (user) => user.is_test_user === true
              );
            }
          }

          // Apply pagination
          const total = filteredUsers.length;
          const startIndex = page * limit;
          const endIndex = startIndex + limit;
          const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

          resolve({
            users: paginatedUsers,

            total,
            page,
            limit,
            totalPages: Math.ceil(total / limit),
          });
        }, 800);
      });
    }

    // Backend API implementation - OPTIMIZED
    try {
      // Prepare filter parameters for RPC
      const searchParam = filters?.search || null;
      const statusParam = filters?.status || null;
      const userTypeParam = filters?.userType || null;
      const countriesParam =
        filters?.countries && filters.countries.length > 0
          ? filters.countries
          : null;
      const startDateParam =
        filters && filters.dateRange && filters.dateRange.startDate
          ? new Date(filters.dateRange.startDate).toISOString()
          : null;
      const endDateParam =
        filters && filters.dateRange && filters.dateRange.endDate
          ? new Date(filters.dateRange.endDate).toISOString()
          : null;
      const lastActivityStartDateParam =
        filters &&
        filters.lastActivityDateRange &&
        filters.lastActivityDateRange.startDate
          ? new Date(filters.lastActivityDateRange.startDate).toISOString()
          : null;
      const lastActivityEndDateParam =
        filters &&
        filters.lastActivityDateRange &&
        filters.lastActivityDateRange.endDate
          ? new Date(filters.lastActivityDateRange.endDate).toISOString()
          : null;
      const summaryFilterParam = filters?.summaryFilter || null;
      const summariesCountFilterParam =
        filters?.summariesCountFilter && filters.summariesCountFilter.length > 0
          ? filters.summariesCountFilter.join(",")
          : null;
      const platformFilterParam = filters?.platformFilter || null;
      const excludeAppleLoginParam = filters?.excludeAppleLogin || false;
      const showOnlyDeletedUsersParam = filters?.showOnlyDeletedUsers || false;
      const showOnlyNonVerifiedUsersParam =
        filters?.showOnlyNonVerifiedUsers || false;
      const sortByParam = filters?.sortBy || "created_at";
      const sortDirectionParam = filters?.sortDirection || "desc";

      // Call backend API with all filtering support
      const params: any = {
        limit,
        page: page,
        search: searchParam,
        status: statusParam,
        user_type: userTypeParam,
        subscription: filters?.subscriptionPlan || null,
        countries: countriesParam,
        start_date: startDateParam,
        end_date: endDateParam,
        last_activity_start_date: lastActivityStartDateParam,
        last_activity_end_date: lastActivityEndDateParam,
        summary: summaryFilterParam,
        summariesCountFilter: summariesCountFilterParam,
        platform: platformFilterParam,
        exclude_apple_login: excludeAppleLoginParam,
        iosVersionFilter: filters?.appVersionFilter?.ios?.enabled
          ? filters.appVersionFilter.ios.targetUsers
          : null,
        androidVersionFilter: filters?.appVersionFilter?.android?.enabled
          ? filters.appVersionFilter.android.targetUsers
          : null,
        excludeInactiveUsers: excludeInactiveUsers,
        show_only_deleted_users: showOnlyDeletedUsersParam,
        show_only_non_verified_users: showOnlyNonVerifiedUsersParam,
        profession_id:
          filters?.professionFilter && String(filters.professionFilter).toLowerCase() !== "all"
            ? filters.professionFilter
            : null,
        sort_by: sortByParam,
        sort_order: sortDirectionParam === "desc" ? "DESC" : "ASC",
      };
      // Remove null/undefined params
      Object.keys(params).forEach((key) =>
        params[key] === null || params[key] === undefined
          ? delete params[key]
          : {}
      );

      // Make API call
      const result = await ApiService.get<{
        users: any[];
        total: number;
      }>(this.ENDPOINTS.USERS, { params });

      // Handle response - check for different possible response structures
      const responseData = (result as any)?.data || result;
      const usersData = responseData?.users || [];
      const totalCount = responseData?.pagination?.total_records || 0;
      const statisticsData = responseData?.statistics || {};
      return {
        users: usersData,
        total: totalCount,
        statistics: statisticsData,
        page,
        limit,
        totalPages: Math.ceil(totalCount / limit),
      };
    } catch (error) {
      console.error("[getUsersOptimized] Error:", error);
      console.error("[getUsersOptimized] Error details:", {
        message: (error as any)?.message,
        response: (error as any)?.response,
        status: (error as any)?.status,
      });
      throw error;
    }
  }

  /**
   * Fallback method using individual calls (for backward compatibility)
   */
  private static async getUsersFallback(
    filters?: Partial<UserFilters>,
    page: number = 1,
    limit: number = 10,
    excludeInactiveUsers: boolean = false
  ): Promise<{
    users: AppUser[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    try {
      // Use the same API call as getUsersOptimized
      return await this.getUsersOptimized(
        filters,
        page,
        limit,
        excludeInactiveUsers
      );
    } catch (error) {
      console.error("[UserService] Fallback query failed:", error);
      return { users: [], total: 0, page, limit, totalPages: 0 };
    }
  }

  /**
   * Get subscription plans for filter dropdown
   * This fetches all active subscription plans from the database
   */
  static async getSubscriptionPlans(
    is_active?: boolean | null,
    platform?: string | null
  ): Promise<{ plan_name: string; plan_id: string }[]> {
    if (config.enableMockData) {
      // Mock implementation
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve([
            { plan_name: "Personal", plan_id: "mock-personal-id" },
            { plan_name: "Pro", plan_id: "mock-pro-id" },
            { plan_name: "Enterprise", plan_id: "mock-enterprise-id" },
            { plan_name: "Basic", plan_id: "mock-basic-id" },
            { plan_name: "Premium", plan_id: "mock-premium-id" },
            { plan_name: "Starter", plan_id: "mock-starter-id" },
            { plan_name: "Business", plan_id: "mock-business-id" },
          ]);
        }, 100);
      });
    }

    // Backend API implementation
    try {
      const plans: any = await ApiService.get<{
        data: {
          plans: {
            name: string;
            id: string;
          }[];
        };
      }>("/get-subscription-plans", {
        params: {
          is_active: is_active,
        },
      });
      return plans?.data?.plans || [];
    } catch (error) {
      console.error("Error in getSubscriptionPlans:", error);
      return [];
    }
  }

  /**
   * Get overall user statistics (independent of pagination/filters)
   * This should be called once to populate the stats cards
   */
  static async getUsersOverallStats(): Promise<{
    totalUsers: number;
    activeUsers: number;
    subscribedUsers: number;
    totalRevenue: number;
    testUsers: number;
    realUsers: number;
  }> {
    if (config.enableMockData) {
      // Mock implementation
      return new Promise((resolve) => {
        setTimeout(() => {
          const totalUsers = mockUsers.length;
          const testUsers = mockUsers.filter(
            (user) => user.is_test_user === true
          ).length;
          const realUsers = mockUsers.filter(
            (user) => user.is_test_user === false
          ).length;
          const activeUsers = mockUsers.filter(
            (user) => user.status === "active" && user.is_test_user === false
          ).length;
          const subscribedUsers = mockUsers.filter(
            (user) =>
              user.subscriptionPlan !== "free" && user.is_test_user === false
          ).length;
          const totalRevenue = mockUsers
            .filter((user) => user.is_test_user === false)
            .reduce((sum, user) => {
              const amount =
                user.subscriptionPlan === "free"
                  ? 0
                  : user.subscriptionPlan === "personal"
                  ? 9.99
                  : 19.99;
              return sum + amount;
            }, 0);

          resolve({
            totalUsers,
            activeUsers,
            subscribedUsers,
            totalRevenue,
            testUsers,
            realUsers,
          });
        }, 300);
      });
    }

    // Backend API implementation
    try {
      const stats = await ApiService.get<{
        totalUsers: number;
        activeUsers: number;
        subscribedUsers: number;
        totalRevenue: number;
        testUsers: number;
        realUsers: number;
      }>("/users/overall-stats");

      return stats;
    } catch (error) {
      console.error("Error in getUsersOverallStats:", error);
      // Fallback to manual calculation
      return this.getUsersOverallStatsFallback();
    }
  }

  /**
   * Fallback method for overall stats (manual calculation)
   */
  private static async getUsersOverallStatsFallback(): Promise<{
    totalUsers: number;
    activeUsers: number;
    subscribedUsers: number;
    totalRevenue: number;
    testUsers: number;
    realUsers: number;
  }> {
    try {
      // Get all users for stats calculation
      const allUsers = await this.getUsers({});

      const totalUsers = allUsers?.length;
      const testUsers = allUsers.filter((user) => user.is_test_user === true)
        .length;
      const realUsers = allUsers.filter((user) => user.is_test_user === false)
        .length;
      const activeUsers = allUsers.filter(
        (user) => user.status === "active" && user.is_test_user === false
      ).length;
      const subscribedUsers = allUsers.filter(
        (user) =>
          user.subscriptionPlan !== "free" && user.is_test_user === false
      ).length;
      const totalRevenue = allUsers
        .filter((user) => user.is_test_user === false)
        .reduce((sum, user) => {
          const amount =
            user.subscriptionPlan === "free"
              ? 0
              : user.subscriptionPlan === "personal"
              ? 9.99
              : 19.99;
          return sum + amount;
        }, 0);

      return {
        totalUsers,
        activeUsers,
        subscribedUsers,
        totalRevenue,
        testUsers,
        realUsers,
      };
    } catch (error) {
      console.error("Error in getUsersOverallStatsFallback:", error);
      return {
        totalUsers: 0,
        activeUsers: 0,
        subscribedUsers: 0,
        totalRevenue: 0,
        testUsers: 0,
        realUsers: 0,
      };
    }
  }

  /**
   * Get all users for CSV export based on filters (no pagination)
   */
  static async getUsersForCSVExport(
    filters: UserFilters
  ): Promise<
    {
      first_name: string;
      last_name: string;
      email: string;
      platform_type: string;
    }[]
  > {
    try {
      // Prepare parameters for the RPC function
      const params: any = {};

      if (filters.search) {
        params.search = filters.search;
      }

      if (filters.status) {
        // Backend expects `user_status` for this filter
        params.user_status = filters.status;
      }

      if (filters.subscriptionPlan) {
        params.subscriptionPlan = filters.subscriptionPlan;
      }

      if (filters.userType) {
        params.user_type = filters.userType;
      }

      if (filters.countries && filters.countries.length > 0) {
        params.countries = filters.countries;
      }

      if (filters.dateRange?.startDate) {
        params.start_date = filters.dateRange.startDate;
      }

      if (filters.dateRange?.endDate) {
        params.end_date = filters.dateRange.endDate;
      }
      if (filters.format) {
        params.format = filters.format;
      }

      // New filter parameters
      if (filters.lastActivityDateRange?.startDate) {
        params.p_last_activity_from = filters.lastActivityDateRange.startDate;
      }

      if (filters.lastActivityDateRange?.endDate) {
        params.p_last_activity_to = filters.lastActivityDateRange.endDate;
      }

      if (filters.summaryFilter) {
        params.p_summary_filter = filters.summaryFilter;
      }

      // Debug: log params to verify CSV export filters
      try {
        console.info("CSV Export RPC params", params);
      } catch (_) {}
      const csvUsers = await ApiService.get<
        {
          first_name: string;
          last_name: string;
          email: string;
          platform_type: string;
        }[]
      >("users/export", { params });

      return csvUsers || [];
    } catch (error) {
      console.error("Error in getUsersForCSVExport:", error);
      throw new Error("Failed to export users to CSV");
    }
  }

  // Extract version from app_version field (e.g., "2.0.3+2" -> "2.0.3")
  private static extractVersionFromAppVersion(
    appVersion: string | undefined
  ): string {
    if (!appVersion) return "";
    return appVersion.split("+")[0];
  }

  // Compare two semantic versions (returns -1, 0, or 1)
  private static compareVersions(version1: string, version2: string): number {
    const v1Parts = version1.split(".").map(Number);
    const v2Parts = version2.split(".").map(Number);

    const maxLength = Math.max(v1Parts.length, v2Parts.length);

    for (let i = 0; i < maxLength; i++) {
      const v1Part = v1Parts[i] || 0;
      const v2Part = v2Parts[i] || 0;

      if (v1Part < v2Part) return -1;
      if (v1Part > v2Part) return 1;
    }

    return 0;
  }
}
